import { compareByNumber, difference } from '@luminovo/commons';
import { colorSystem, Dot, Text, Tooltip } from '@luminovo/design-system';
import { styled } from '@mui/material';
import React from 'react';

import {
    BAR_HEIGHT,
    type BarExtent,
    barScale,
    Row,
    ROW_HEIGHT,
    RowButton,
    Rows,
    Segment,
    Track,
    ZeroLine,
} from '../chartRows';
import { type ChartDatum, type StackedBarChartProps } from '../types';
import { ChartTooltip } from './ChartTooltip';

// Stacked segments touch, so a white hairline separates them.
const StackedSegment = styled(Segment)({ boxShadow: `0 0 0 0.5px ${colorSystem.neutral.white}` });

/** Renders its title only when the tooltip opens, so closed rows don't build their breakdown or subtitle. */
function LazyTitle({ render }: { render: () => React.ReactNode }) {
    return <>{render()}</>;
}

export function StackedBarChart<TKeys extends string>({
    data: inputData,
    keys,
    seriesMeta,
    formatValue = (v) => String(v),
    unstackedKey,
    stacked = true,
    onBarClick,
    tooltipSubtitle,
    selectedKeys: externalSelectedKeys,
}: StackedBarChartProps<TKeys>) {
    const scrollRef = React.useRef<HTMLDivElement>(null);
    const activeKeys = externalSelectedKeys ?? keys;

    React.useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
    }, [inputData]);

    const { data, rowKeys } = React.useMemo(() => {
        const hiddenKeys = difference(keys, activeKeys);
        // Keyed by the input order, so re-sorting moves rows instead of remounting them.
        const rowKeys = new Map<ChartDatum<TKeys>, string>();
        const cleared = inputData.map((datum, index) => {
            const copy = { ...datum };
            for (const key of hiddenKeys) {
                (copy as Record<string, unknown>)[key] = 0;
            }
            rowKeys.set(copy, `${index}-${datum.label}`);
            return copy;
        });

        if (activeKeys.length === 1 && keys.length > 1) {
            cleared.sort(compareByNumber((d) => -Number(d[activeKeys[0]] ?? 0)));
        }

        return { data: cleared, rowKeys };
    }, [inputData, keys, activeKeys]);

    const valueKeys = keys.filter((k) => k !== unstackedKey && !seriesMeta[k]?.status);
    const statusKeys = keys.filter((k) => seriesMeta[k]?.status);
    const value = (datum: ChartDatum<TKeys>, key: TKeys) => Number(datum[key] ?? 0);

    // A row's bar: every value series stacked from zero, or only the longest one when the values don't add up.
    const segmentsOf = (datum: ChartDatum<TKeys>): TKeys[] => {
        const nonZero = valueKeys.filter((k) => value(datum, k) !== 0);
        if (stacked || nonZero.length === 0) {
            return nonZero;
        }
        return [
            nonZero.reduce((longest, k) => (Math.abs(value(datum, k)) > Math.abs(value(datum, longest)) ? k : longest)),
        ];
    };

    const extents = data.map((datum): BarExtent => {
        const values = segmentsOf(datum).map((k) => value(datum, k));
        const secondary = unstackedKey ? value(datum, unstackedKey) : 0;
        return {
            negative: values.filter((v) => v < 0).reduce((a, b) => a + b, 0),
            positive: Math.max(
                values.filter((v) => v > 0).reduce((a, b) => a + b, 0),
                secondary,
            ),
        };
    });
    const scale = barScale(extents);
    const hasNegatives = extents.some((e) => e.negative < 0);

    return (
        <div ref={scrollRef} style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
            <Rows>
                {data.map((datum) => {
                    const segments = segmentsOf(datum);
                    const total = segments.reduce((sum, k) => sum + value(datum, k), 0);
                    const formattedTotal = segments.length > 0 ? formatValue(total) : '';
                    const statuses = segments.length === 0 ? statusKeys.filter((k) => value(datum, k) > 0) : [];

                    let positiveOffset = 0;
                    let negativeOffset = 0;
                    const bars = segments.map((key) => {
                        const v = value(datum, key);
                        const width = scale.percent(v);
                        const left = v >= 0 ? scale.zero + positiveOffset : scale.zero - negativeOffset - width;
                        if (v >= 0) positiveOffset += width;
                        else negativeOffset += width;
                        return (
                            <StackedSegment
                                key={key}
                                style={{ left: `${left}%`, width: `${width}%`, background: seriesMeta[key]?.color }}
                            />
                        );
                    });

                    const cells = (
                        <>
                            <Text
                                variant="body-small"
                                color={colorSystem.neutral[8]}
                                showEllipsis="ellipsisOnly"
                                style={{ display: 'block', maxWidth: 320 }}
                            >
                                {datum.label}
                            </Text>
                            <Track>
                                {hasNegatives && <ZeroLine style={{ left: `${scale.zero}%` }} />}
                                {bars}
                                {unstackedKey && value(datum, unstackedKey) > 0 && (
                                    <StackedSegment
                                        style={{
                                            left: `${scale.zero}%`,
                                            width: `${scale.percent(value(datum, unstackedKey))}%`,
                                            top: (ROW_HEIGHT + BAR_HEIGHT) / 2 + 2,
                                            height: 3,
                                            background: seriesMeta[unstackedKey]?.color,
                                        }}
                                    />
                                )}
                                {statuses.length > 0 && (
                                    <span
                                        style={{
                                            position: 'absolute',
                                            inset: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '12px',
                                        }}
                                    >
                                        {statuses.map((key) => (
                                            <span
                                                key={key}
                                                style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                                            >
                                                <Dot color={seriesMeta[key]?.color ?? colorSystem.neutral[5]} />
                                                <Text variant="body-small" color={colorSystem.neutral[7]}>
                                                    {seriesMeta[key]?.label}
                                                </Text>
                                            </span>
                                        ))}
                                    </span>
                                )}
                            </Track>
                            <Text
                                variant="body-small"
                                color={colorSystem.neutral[8]}
                                tabularNums
                                style={{ textAlign: 'right' }}
                            >
                                {formattedTotal}
                            </Text>
                        </>
                    );

                    const statusLabels = statuses.map((key) => seriesMeta[key]?.label).join(', ');
                    const name = [datum.label, formattedTotal || statusLabels].filter(Boolean).join(' ');
                    const row = onBarClick ? (
                        <RowButton type="button" aria-label={name} onClick={() => onBarClick(datum)}>
                            {cells}
                        </RowButton>
                    ) : (
                        <Row>{cells}</Row>
                    );

                    return (
                        <Tooltip
                            key={rowKeys.get(datum)}
                            variant="white"
                            disableMaxWidth
                            describeChild
                            disableInteractive
                            placement="top"
                            // MUI opens any element title, even one that renders nothing.
                            title={
                                keys.some((key) => value(datum, key) !== 0) ? (
                                    <LazyTitle
                                        render={() => (
                                            <ChartTooltip
                                                active
                                                label={datum.label}
                                                subtitle={tooltipSubtitle?.(datum)}
                                                payload={keys.map((key) => ({
                                                    dataKey: key,
                                                    value: value(datum, key),
                                                    payload: datum,
                                                }))}
                                                seriesMeta={seriesMeta}
                                                formatValue={formatValue}
                                                visibleKeys={activeKeys}
                                                unstackedKey={unstackedKey}
                                                stacked={stacked}
                                            />
                                        )}
                                    />
                                ) : (
                                    ''
                                )
                            }
                        >
                            {row}
                        </Tooltip>
                    );
                })}
            </Rows>
        </div>
    );
}
